import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { chatbot as chatbotApi, userPrefs as prefsApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChatbotLauncher, ChatbotChaseBubble } from "./chatbot/ChatbotLauncher";
import { ChatbotHeader } from "./chatbot/ChatbotHeader";
import { ChatbotPicker } from "./chatbot/ChatbotPicker";
import { ChatbotMessages } from "./chatbot/ChatbotMessages";
import { ChatbotInput } from "./chatbot/ChatbotInput";
import { ChatbotFooter } from "./chatbot/ChatbotFooter";
import { loadStoredRole, personaForRole, saveStoredRole } from "./chatbot/personaRoles";

import { log } from "@/lib/log";
const STORAGE_KEY = "rp_chatbot_state_v1";
const SESSION_KEY = "rp_chatbot_chase_shown_v1";

// Tiny client-side gender heuristic for Indian first names.
// Used ONLY to recommend a default persona (opposite-gender mentor pairing).
// If unknown → returns null, picker shows all 4 personas equally.
const FEMALE_HINTS = [
  "priya","anita","aarti","arti","kavya","kavita","meera","neha","pooja","preeti","priti","ritu","rashmi","rekha","sneha","shruti","sonal","suman","sunita","swati","vidya","manisha","megha","nidhi","payal","radhika","seema","shilpa","tanya","vandana","aditi","akshara","akshita","ananya","anjali","anushka","aparna","asha","bhavna","chitra","deepa","deepika","divya","gauri","geeta","heena","ishita","jaya","kajal","kalpana","komal","lakshmi","mala","mona","muskan","namrata","nandini","nisha","palak","poonam","pratibha","rachna","rajni","richa","rina","rita","ruchi","sakshi","sangeeta","saroj","saumya","savita","shalini","sharmila","sheetal","shobha","shweta","simran","smita","sushma","tanvi","trupti","uma","usha","vaishali","varsha","vinita","yamini","yashashri","zeenat","kiran","jyoti","manjusha",
];
const MALE_HINTS = [
  "rishi","arjun","amit","abhishek","aditya","akash","ajay","akhil","alok","aman","anand","anil","ankit","ankur","anurag","anuj","arun","ashish","ashok","aviral","bharat","chetan","deepak","dhruv","dinesh","gaurav","girish","harish","harsh","hemant","hitesh","jay","jitesh","kabir","kapil","karan","kartik","kunal","lalit","mahesh","manish","mayank","mohit","mukesh","nakul","naman","narendra","nikhil","nilesh","nitin","pankaj","pranav","prashant","prateek","praveen","puneet","raghav","rahul","rajat","rajesh","rakesh","ramesh","ravi","rohan","rohit","sachin","sagar","sandeep","sanjay","sanjeev","saurabh","shashank","shashi","shyam","siddharth","sumit","sunil","suresh","tarun","tushar","ujjwal","umesh","varun","vibhor","vijay","vikas","vikram","vinay","vinod","vipul","virat","vishal","vivek","yash","yogesh","aakash","aayush","abhinav","raman","rajiv","kishore","srinivas","ramakrishna","krishna",
];

function guessGender(name) {
  if (!name) return null;
  const first = String(name).trim().split(/\s+/)[0]?.toLowerCase();
  if (!first) return null;
  if (FEMALE_HINTS.includes(first)) return "female";
  if (MALE_HINTS.includes(first)) return "male";
  // ending-based weak heuristic
  if (/[aeiou]$/.test(first) && first.endsWith("a")) return "female";
  return null;
}

// Map gender → recommended persona (opposite-gender mentor pairing per spec).
function recommendedPersonaFor(gender) {
  if (gender === "male") return "priya";
  if (gender === "female") return "rishi";
  return null;
}

function loadStored() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return {};
    const s = JSON.parse(raw);
    return {
      personaId: s.personaId || null,
      sessionId: s.sessionId || null,
      messages: Array.isArray(s.messages) ? s.messages : [],
      step: s.step === "chat" && s.personaId ? "chat" : "picker",
    };
  } catch {
    return {};
  }
}

let _msgSeq = 0;
function newMsgId() {
  _msgSeq += 1;
  return `m_${Date.now().toString(36)}_${_msgSeq}`;
}

export default function ChatbotWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [personas, setPersonas] = useState([]);
  const [open, setOpen] = useState(false);
  const stored = useMemo(() => loadStored(), []);
  const [step, setStep] = useState(stored.step || "picker"); // picker | chat
  const [personaId, setPersonaId] = useState(stored.personaId || null);
  const [messages, setMessages] = useState(stored.messages || []);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(stored.sessionId || null);
  const [chase, setChase] = useState(false);
  const [role, setRole] = useState(() => loadStoredRole());
  const [expanded, setExpanded] = useState(false);
  const [rolePickerOpen, setRolePickerOpen] = useState(false);
  const scrollerRef = useRef(null);

  const recommended = useMemo(() => {
    // 1. Role pick wins (explicit signal from user).
    const byRole = personaForRole(role);
    if (byRole) return byRole;
    // 2. Fallback to name-based gender heuristic for logged-in users.
    if (!user?.name) return null;
    return recommendedPersonaFor(guessGender(user.name));
  }, [role, user]);

  // Fetch personas on mount.
  useEffect(() => {
    chatbotApi.personas().then((d) => setPersonas(d.personas || [])).catch(() => {});
  }, []);

  // Hydrate role from backend for logged-in users (server is source of truth,
  // localStorage is fast cache for anonymous + first-paint speed).
  useEffect(() => {
    if (!user) return;
    prefsApi.get()
      .then((p) => {
        const serverRole = p?.chatbot_role || null;
        if (serverRole) {
          setRole(serverRole);
          saveStoredRole(serverRole);
        }
      })
      .catch(() => { /* offline / not logged in — ignore */ });
  }, [user]);

  // Centralised role setter — writes localStorage immediately, syncs to
  // backend in the background for logged-in users.
  const updateRole = useCallback((nextRole) => {
    setRole(nextRole);
    saveStoredRole(nextRole);
    if (user) {
      prefsApi.update({ chatbot_role: nextRole || "" }).catch((err) => {
        log.warn("[chatbot] role backend sync failed:", err?.message);
      });
    }
  }, [user]);

  // Persist session state.
  useEffect(() => {
    try {
      window.localStorage?.setItem(STORAGE_KEY, JSON.stringify({
        personaId, sessionId, messages, step,
      }));
    } catch { /* ignore */ }
  }, [personaId, sessionId, messages, step]);

  // Auto-scroll to bottom on new message.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  // Exit-intent "chasing bubble" — only on landing page, for anonymous users, once per session.
  useEffect(() => {
    if (user) return;
    if (location.pathname !== "/") return;
    if (window.sessionStorage?.getItem(SESSION_KEY)) return;
    let armed = true;
    const onLeave = (e) => {
      if (!armed) return;
      if (e.clientY <= 0) {
        armed = false;
        setChase(true);
        try { window.sessionStorage?.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
      }
    };
    document.addEventListener("mouseleave", onLeave);
    // Fallback timer: trigger after 45s of idle on landing if not opened
    const t = setTimeout(() => {
      if (armed && !open) {
        armed = false;
        setChase(true);
        try { window.sessionStorage?.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
      }
    }, 45000);
    return () => {
      document.removeEventListener("mouseleave", onLeave);
      clearTimeout(t);
    };
  }, [user, location.pathname, open]);

  const launch = useCallback((pid = null) => {
    setChase(false);
    setOpen(true);
    if (pid) {
      setPersonaId(pid);
      setStep("chat");
    } else if (personaId) {
      setStep("chat");
    } else {
      setStep("picker");
    }
  }, [personaId]);

  // Allow other pages (e.g. the Help "Ask Rishi" CTA) to programmatically
  // open the chatbot, optionally with a preferred persona.
  useEffect(() => {
    const onOpen = (e) => {
      const wantPersona = e?.detail?.persona;
      if (wantPersona && personas.find((p) => p.id === wantPersona)) {
        launch(wantPersona);
      } else {
        launch();
      }
    };
    window.addEventListener("riskpedia:open-chatbot", onOpen);
    return () => window.removeEventListener("riskpedia:open-chatbot", onOpen);
  }, [launch, personas]);

  const pickPersona = (pid) => {
    setPersonaId(pid);
    setStep("chat");
    if (messages.length === 0) {
      const p = personas.find((x) => x.id === pid);
      if (p) {
        setMessages([{
          id: newMsgId(),
          role: "assistant",
          content: `Hi, I'm ${p.name} — ${p.role}. ${p.tagline} How can I help you today?`,
        }]);
      }
    }
  };

  const newSession = () => {
    setMessages([]);
    setSessionId(null);
    setStep("picker");
    setPersonaId(null);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending || !personaId) return;
    setSending(true);
    setInput("");
    const next = [...messages, { id: newMsgId(), role: "user", content: text }];
    setMessages(next);
    try {
      const body = {
        persona: personaId,
        message: text,
        history: messages
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map(({ role, content }) => ({ role, content }))
          .slice(-16),
        session_id: sessionId || undefined,
      };
      const res = await chatbotApi.send(body);
      setSessionId(res.session_id);
      setMessages((m) => [...m, {
        id: newMsgId(),
        role: "assistant",
        content: res.reply,
        suggestions: res.suggestions || [],
        log_id: res.log_id,
      }]);
    } catch (err) {
      setMessages((m) => [...m, {
        id: newMsgId(),
        role: "assistant",
        content: "Sorry — I had trouble responding just now. Please try again in a moment.",
      }]);
      log.warn("[chatbot] send failed:", err?.response?.data || err?.message);
    } finally {
      setSending(false);
    }
  };

  const trackClick = (sug, msg) => {
    // Fire-and-forget click logging.
    chatbotApi.click({
      url: sug.url,
      title: sug.title,
      type: sug.type,
      persona: personaId,
      session_id: sessionId,
      log_id: msg?.log_id,
    }).catch(() => { /* ignore */ });
  };

  const activePersona = personas.find((p) => p.id === personaId);

  return (
    <>
      {!open && <ChatbotLauncher onOpen={() => launch()} />}

      {chase && !open && (
        <ChatbotChaseBubble onOpen={() => launch()} onDismiss={() => setChase(false)} />
      )}

      {open && (
        <div
          className={
            expanded
              ? "fixed inset-2 sm:inset-4 z-50 flex flex-col rounded-2xl border border-rp-border bg-rp-surface shadow-2xl shadow-black/40 overflow-hidden"
              : "fixed bottom-2 left-2 right-2 sm:right-auto sm:bottom-4 sm:left-4 z-50 sm:w-[400px] flex flex-col rounded-2xl border border-rp-border bg-rp-surface shadow-2xl shadow-black/40 overflow-hidden"
          }
          style={expanded ? undefined : {
            // Auto-fit to available viewport — header AND footer must always
            // be visible. `dvh` accounts for mobile browser chrome that
            // changes height when address bar collapses.
            height: "min(640px, calc(100dvh - 1rem))",
            maxHeight: "calc(100dvh - 1rem)",
          }}
          data-testid="chatbot-panel"
          data-expanded={expanded ? "true" : "false"}
        >
          <ChatbotHeader
            step={step}
            activePersona={activePersona}
            onBack={() => setStep("picker")}
          />

          {step === "picker" ? (
            <ChatbotPicker
              personas={personas}
              recommended={recommended}
              role={role}
              onRoleChange={updateRole}
              onPick={pickPersona}
            />
          ) : (
            <>
              <ChatbotMessages
                ref={scrollerRef}
                messages={messages}
                sending={sending}
                activePersonaName={activePersona?.name}
                expanded={expanded}
                onTrackClick={trackClick}
              />
              <ChatbotInput
                input={input}
                setInput={setInput}
                sending={sending}
                activePersonaName={activePersona?.name}
                expanded={expanded}
                onSend={send}
              />
            </>
          )}

          <ChatbotFooter
            step={step}
            messageCount={messages.length}
            expanded={expanded}
            rolePickerOpen={rolePickerOpen}
            role={role}
            onNewSession={newSession}
            onToggleRolePicker={() => setRolePickerOpen((v) => !v)}
            onRoleChange={updateRole}
            onToggleExpand={() => setExpanded((v) => !v)}
            onMinimize={() => { setOpen(false); setRolePickerOpen(false); }}
          />
        </div>
      )}
    </>
  );
}
